import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { create } from '@bufbuild/protobuf';
import { PlusIcon, PencilIcon, TrashIcon, LoaderIcon } from '@/components/icons';
import { toast } from 'sonner';
import { fullErrorText } from '@/api/errors';
import { Button } from '@/components/ui/button';
import { UserName } from '@/components/UserName';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { sandboxApi } from '@/api/sandbox.api';
import { sandboxClassApi } from '@/api/sandboxClass.api';
import { formatTimestamp } from '@/utils/utils';
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { SandboxTypeBadge, ServerIcon, EmptyBoxIcon, ErrorCircleIcon } from './shared';

export default function SandboxInstances() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchKeyword, setSearchKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [deleteUid, setDeleteUid] = useState<string | null>(null);

  // 输入防抖：停止输入 300ms 后再触发后端模糊查询，并回到第 1 页。
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedKeyword(searchKeyword.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchKeyword]);

  const { data: classesData } = useQuery({
    queryKey: ['sandbox-classes'],
    queryFn: () =>
      sandboxClassApi.listSandboxClasses(create(ListRequestSchema, { page: 1, pageSize: 200 })),
  });

  const {
    data: instancesData,
    isLoading: instancesLoading,
    error: instancesError,
    refetch: instancesRefetch,
  } = useQuery({
    queryKey: ['sandboxes', page, pageSize, debouncedKeyword],
    queryFn: () =>
      sandboxApi.listSandboxes(
        create(ListRequestSchema, {
          page,
          pageSize,
          fuzzy: debouncedKeyword ? { name: debouncedKeyword } : {},
        })
      ),
  });

  const deleteMutation = useMutation({
    mutationFn: (uid: string) => sandboxApi.deleteSandbox(uid),
    onSuccess: () => {
      setDeleteUid(null);
      queryClient.invalidateQueries({ queryKey: ['sandboxes'] });
    },
    onError: (error) => toast.error(`删除失败：${fullErrorText(error)}`),
  });

  const classes = (classesData?.data || []).filter(Boolean);
  const instances = (instancesData?.data || []).filter(Boolean);

  // 模糊查询已下沉到后端 list 接口（fuzzy.name），此处不再本地过滤。
  const keyword = debouncedKeyword;

  const classMap = new Map(classes.map((c) => [c.id, c.name]));
  const totalPages = Math.max(1, Math.ceil((instancesData?.total || 0) / pageSize));

  return (
    <>
      <div className="bg-background rounded-xl border shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <ServerIcon />
            <span className="text-sm font-medium text-foreground">实例</span>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <Input
                placeholder="搜索名称..."
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                className="pl-9 h-9 w-48 bg-muted/50 border-0 focus:bg-background"
              />
            </div>
            <Button
              size="sm"
              onClick={() => navigate('/plugins/sandboxes/create-instance')}
              className="gap-1.5"
            >
              <PlusIcon className="h-4 w-4" />
              新建
            </Button>
          </div>
        </div>

        {instancesLoading ? (
          <div className="divide-y">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-4 px-5 py-4">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-24" />
                <div className="ml-auto flex gap-2">
                  <Skeleton className="h-8 w-8" />
                  <Skeleton className="h-8 w-8" />
                </div>
              </div>
            ))}
          </div>
        ) : instancesError ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
              <span className="text-destructive">
                <ErrorCircleIcon />
              </span>
            </div>
            <p className="font-semibold text-foreground mb-1">加载失败</p>
            <p className="text-sm text-muted-foreground mb-5">请检查网络连接后重试</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => instancesRefetch()}
              className="gap-1.5"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              重试
            </Button>
          </div>
        ) : instances.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <EmptyBoxIcon />
            <p className="font-semibold text-foreground mt-4 mb-1">
              {keyword ? '没有找到匹配的沙箱实例' : '暂无沙箱实例'}
            </p>
            <p className="text-sm text-muted-foreground mb-5">
              {keyword ? '尝试其他关键词搜索' : '创建第一个沙箱实例来开始使用'}
            </p>
            {!keyword && (
              <Button
                size="sm"
                onClick={() => navigate('/plugins/sandboxes/create-instance')}
                className="gap-1.5"
              >
                <PlusIcon className="h-4 w-4" />
                新建实例
              </Button>
            )}
          </div>
        ) : (
          <Table className="table-fixed w-full">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="h-11 w-[240px]">名称</TableHead>
                <TableHead className="h-11 w-28">类型</TableHead>
                <TableHead className="h-11">沙箱模版</TableHead>
                <TableHead className="h-11">创建者</TableHead>
                <TableHead className="h-11 w-28">创建时间</TableHead>
                <TableHead className="h-11 w-28">更新时间</TableHead>
                <TableHead className="h-11 w-24">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {instances.map((item) => {
                const className = item.classId ? classMap.get(item.classId) || item.classId : '-';
                return (
                  <TableRow
                    key={item.id}
                    className="group cursor-pointer"
                    onClick={() => navigate(`/plugins/sandboxes/instances/${item.id}`)}
                  >
                    <TableCell className="font-medium py-4 overflow-hidden">
                      <div className="min-w-0" title={item.name}>
                        <p
                          className="text-primary hover:text-primary/80 font-medium text-left truncate cursor-pointer m-0"
                          onClick={() => navigate(`/plugins/sandboxes/instances/${item.id}`)}
                        >
                          {item.name}
                        </p>
                        <span className="text-xs text-muted-foreground/60 truncate block">
                          ID: {item.id}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="py-3">
                      <SandboxTypeBadge type={item.type} />
                    </TableCell>
                    <TableCell className="py-3">
                      <span className="text-sm text-muted-foreground">{className}</span>
                    </TableCell>
                    <TableCell className="py-3">
                      <span className="text-sm text-muted-foreground">
                        <UserName userId={item.userId} />
                      </span>
                    </TableCell>
                    <TableCell className="py-3 text-muted-foreground text-sm">
                      {formatTimestamp(item.createdAt)}
                    </TableCell>
                    <TableCell className="py-3 text-muted-foreground text-sm">
                      {formatTimestamp(item.updatedAt)}
                    </TableCell>
                    <TableCell className="py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-foreground"
                          onClick={() => {
                            if (item.classId) {
                              navigate(
                                `/plugins/sandboxes/create-instance-from-class/${item.classId}?instanceId=${item.id}`
                              );
                            } else {
                              navigate(`/plugins/sandboxes/instances/${item.id}/edit`);
                            }
                          }}
                        >
                          <PencilIcon className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={() => setDeleteUid(item.id)}
                        >
                          <TrashIcon className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {!instancesLoading && !instancesError && instances.length > 0 && (
          <div className="flex items-center justify-between px-5 py-4 border-t bg-muted/10 gap-4">
            <p className="text-sm text-muted-foreground">
              共 <span className="font-medium text-foreground">{instancesData?.total || 0}</span> 条
              {keyword && <span className="ml-1">（搜索“{keyword}”）</span>}
            </p>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground">每页</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="h-7 rounded-md border border-input bg-background px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-xs text-muted-foreground">条</span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </Button>
                <div className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-background border text-xs">
                  <span className="font-medium">{page}</span>
                  <span className="text-muted-foreground">/</span>
                  <span className="text-muted-foreground">{totalPages}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteUid} onOpenChange={() => setDeleteUid(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除</AlertDialogTitle>
            <AlertDialogDescription>
              确定要删除该沙箱实例吗？此操作不可撤销。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteUid(null)}>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteUid) {
                  deleteMutation.mutate(deleteUid);
                }
              }}
              disabled={deleteMutation.isPending}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleteMutation.isPending && <LoaderIcon className="mr-2 h-4 w-4" />}
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
